import { useEffect, useState } from "react";
import { Sun, Sunrise, Plus, Bell } from "lucide-react";
import AppointmentCard from "@/components/admin/AppointmentCard";
import NewAppointmentDialog from "@/components/admin/NewAppointmentDialog";
import { useAppointments, toKey } from "@/components/admin/useAppointments";

const longDate = (d) => d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });

export default function TodayPanel({ headers, goTo }) {
  const { items, services, load, update, remove, add, replace, reviewLink, resend } = useAppointments(headers);
  const [showNew, setShowNew] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const now = new Date();
  const tomorrow = new Date(now.getTime() + 86400000);
  const tKey = toKey(now);
  const mKey = toKey(tomorrow);
  const live = (a) => a.status !== "cancelada";
  const today = (items || []).filter((a) => a.date === tKey && live(a));
  const next = (items || []).filter((a) => a.date === mKey && live(a));
  const pending = (items || []).filter((a) => a.status === "pendiente").length;
  const revenue = today.filter((a) => a.status === "completada").reduce((s, a) => s + (a.price || 0), 0);

  return (
    <section data-testid="admin-today-panel">
      <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium capitalize">{longDate(now)}</p>
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian mt-1">Hola Isi, esto es lo que tienes hoy</h1>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <Stat testid="today-stat-count" label="Citas hoy" value={today.length} />
        <Stat testid="today-stat-done" label="Han venido" value={today.filter((a) => a.status === "completada").length} />
        <Stat testid="today-stat-revenue" label="Caja de hoy" value={`${revenue.toLocaleString("es-ES")} €`} />
        <button onClick={() => goTo("citas")} data-testid="today-stat-pending" className="text-left rounded-2xl bg-creamcard border border-sand p-4 hover:border-gold/60 transition-colors">
          <p className="text-[10px] tracking-[0.2em] uppercase text-taupe flex items-center gap-1"><Bell className="w-3 h-3" /> Por confirmar</p>
          <p className={`font-display text-2xl mt-1 ${pending ? "text-gold" : "text-obsidian"}`}>{pending}</p>
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        <button onClick={() => setShowNew(true)} data-testid="today-new-appointment-btn" className="inline-flex items-center gap-2 rounded-xl bg-obsidian hover:bg-cocoa text-white text-sm font-medium px-5 py-2.5 transition-colors">
          <Plus className="w-4 h-4" /> Nueva cita
        </button>
      </div>
      <NewAppointmentDialog open={showNew} onClose={() => setShowNew(false)} onCreated={(a) => { add(a); load(); }} headers={headers} />

      <Block Icon={Sun} title="Hoy" testid="today-list" empty="Hoy no tienes citas. ¡Día para ti!">
        {items === null ? <Skeleton /> : today.map((a) => <AppointmentCard key={a.id} a={a} services={services} onUpdate={update} onDelete={remove} onEdited={(u) => { replace(u); load(); }} headers={headers} reviewLink={reviewLink} onResend={resend} compact />)}
      </Block>

      <Block Icon={Sunrise} title={`Mañana · ${longDate(tomorrow)}`} testid="tomorrow-list" empty="Mañana no tienes citas todavía." hint="Envíales el recordatorio por WhatsApp con un toque.">
        {items === null ? <Skeleton /> : next.map((a) => <AppointmentCard key={a.id} a={a} services={services} onUpdate={update} onDelete={remove} onEdited={(u) => { replace(u); load(); }} headers={headers} reviewLink={reviewLink} onResend={resend} compact />)}
      </Block>
    </section>
  );
}

function Block({ Icon, title, testid, empty, hint, children }) {
  const list = Array.isArray(children) ? children : children ? [children] : [];
  return (
    <div className="mt-8" data-testid={testid}>
      <h2 className="font-display text-xl text-obsidian flex items-center gap-2 capitalize"><Icon className="w-5 h-5 text-gold" /> {title}</h2>
      {hint && list.length > 0 && <p className="text-xs text-taupe mt-1">{hint}</p>}
      {list.length === 0 ? (
        <p data-testid={`${testid}-empty`} className="mt-3 rounded-2xl border border-dashed border-sand bg-creamcard p-6 text-sm text-taupe">{empty}</p>
      ) : (
        <div className="grid gap-3 mt-3">{children}</div>
      )}
    </div>
  );
}

function Stat({ testid, label, value }) {
  return (
    <div data-testid={testid} className="rounded-2xl bg-creamcard border border-sand p-4">
      <p className="text-[10px] tracking-[0.2em] uppercase text-taupe">{label}</p>
      <p className="font-display text-2xl text-obsidian mt-1">{value}</p>
    </div>
  );
}

function Skeleton() {
  return <div className="h-24 rounded-2xl bg-sand/50 animate-pulse" />;
}
